import {
  Autocomplete,
  Badge,
  Chip,
  IconButton,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icon } from '@common-components';
import ManageLabelsPopover from 'forms/manage-labels/ManageLabelsPopover';
import { useGetUserLabels, useMenu } from 'hooks';
import { AutoCompleteLabel } from 'types';
import { filterOptions, mapLabelsAutocompleteOptions } from 'utils';

interface OptionLabelSelectorProps {
  fieldName: string;
}

const OptionLabelSelector = ({ fieldName }: OptionLabelSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { control, setValue } = useFormContext();
  const { labels: userLabels } = useGetUserLabels();
  const { openMenu, ...restMenuProps } = useMenu();

  const labelIds = useWatch({
    control,
    name: fieldName,
  }) as string[] | undefined;

  // Ensure labels field is initialized with empty array if it's null/undefined
  React.useEffect(() => {
    if (labelIds === null || labelIds === undefined) {
      setValue(fieldName, [], { shouldDirty: false });
    }
  }, [labelIds, fieldName, setValue]);

  const autoCompleteOptions = mapLabelsAutocompleteOptions(userLabels);

  // Convert label IDs to AutoCompleteLabel objects for display
  const selectedLabels = React.useMemo(() => {
    if (!labelIds || !userLabels) {
      return [];
    }

    const result = labelIds
      .map((id) => {
        const userLabel = userLabels.find((label) => label.id === id);

        if (userLabel) {
          return {
            value: userLabel.name,
            label: userLabel.name,
            color: userLabel.color,
            icon: userLabel.icon,
          };
        }
        // For labels that don't exist in userLabels (new ones), create a basic object
        return {
          value: id,
          label: id,
          color: '#default',
        };
      })
      .filter(Boolean) as AutoCompleteLabel[];

    return result;
  }, [labelIds, userLabels]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLabelsChange = (newLabels: AutoCompleteLabel[]) => {
    // Convert AutoCompleteLabel objects back to label IDs
    const newLabelIds = newLabels.map((label) => {
      // Find the corresponding user label by name
      const userLabel = userLabels.find((ul) => ul.name === label.value || ul.name === label.label);
      return userLabel?.id || label.value; // Return ID if found, otherwise the value (for new labels)
    });
    setValue(fieldName, newLabelIds, { shouldDirty: true });
  };

  const open = Boolean(anchorEl);
  const labelCount = labelIds?.length || 0;

  return (
    <>
      <Badge badgeContent={labelCount} color='primary' showZero={false}>
        <IconButton
          size='small'
          onClick={handleClick}
          sx={{
            color: labelCount > 0 ? 'primary.main' : 'action.active',
          }}
        >
          <Icon icon='label' fontSize='small' />
        </IconButton>
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            maxWidth: 400,
          },
        }}
      >
        <Stack gap={2}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='subtitle2'>Labels</Typography>
            <IconButton size='small' onClick={openMenu}>
              <Icon icon='settings' fontSize='small' />
            </IconButton>
          </Stack>
          <Autocomplete
            freeSolo
            multiple
            filterSelectedOptions
            filterOptions={filterOptions}
            options={autoCompleteOptions}
            value={selectedLabels}
            onChange={(_, newValue) => {
              handleLabelsChange(newValue as AutoCompleteLabel[]);
            }}
            isOptionEqualToValue={(option, value) =>
              option.value.toLowerCase() === value.value.toLowerCase()
            }
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option.label || option.value;
            }}
            renderOption={(props, option) => (
              <MenuItem key={option.value} {...props}>
                <ListItemText disableTypography>
                  <Chip size='small' label={option.label} sx={{ backgroundColor: option.color }} />
                </ListItemText>
              </MenuItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Add Labels, Type to Create'
                size='small'
                variant='outlined'
              />
            )}
          />
        </Stack>
      </Popover>
      <ManageLabelsPopover menuProps={restMenuProps} />
    </>
  );
};

export default OptionLabelSelector;
